import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { useSnackbar } from 'notistack';
import { Box, Checkbox, Divider, FormControl, FormControlLabel, FormHelperText, IconButton, MenuItem, Select, Tooltip } from "@mui/material"
import Grid from "@mui/material/Grid2"
import CloseIcon from '@mui/icons-material/Close';
import { useTrackedUser } from "./UserState"
import { AXIOS_API_INSTANCE } from "../Constants"
import { send_authorized_request } from "../Constants/cognito"
import { LoadingBackdropIndeterminate } from "./LoadingBackdrop"

const service_manager_notifications = [
    {
        'name': 'service_order_status_threshold_breach',
        'short_name': 'SO Stale',
        'tooltip': 'Be notified when a service order stays in a status too long',
    },
    {
        'name': 'service_order_status_events',
        'short_name': 'SO Status Changes',
        'tooltip': 'Be notified when Service Orders change status to a status configured to notify Service Managers',
    },
    {
        'name': 'part_status_threshold_breach',
        'short_name': 'Parts Stale',
        'tooltip': 'Be notified when a part stays in a status too long',
    },
    {
        'name': 'parts_ready',
        'short_name': 'Parts Ready',
        'tooltip': 'Be notified when all parts are ready for a service',
    },
    {
        'name': 'part_status_events',
        'short_name': 'Parts Status Changes',
        'tooltip': 'Be notified when parts change status to a status configured to notify Service Managers',
    },
    {
        'name': 'customer_approval_response',
        'short_name': 'Customer Approval',
        'tooltip': 'Be notified when a customer responds to a request for approval',
    },
    {
        'name': 'update_location',
        'short_name': 'Location Transfer',
        'tooltip': 'Be notified when a service order is transferred to a different location',
    },
    {
        'name': 'invoice_paid',
        'short_name': 'Invoice Paid',
        'tooltip': 'Be notified when an invoice is completely paid',
    },
    {
        'name': 'online_payment',
        'short_name': 'Online Payment',
        'tooltip': 'Be notified when a payment is completed online',
    },
]

const service_writer_notifications = [
    {
        'name': 'service_order_status_threshold_breach',
        'short_name': 'SO Stale',
        'tooltip': 'Be notified when a service order stays in a status too long',
    },
    {
        'name': 'service_order_status_events',
        'short_name': 'SO Status Changes',
        'tooltip': 'Be notified when Service Orders change status to a status configured to notify Service Writers',
    },
    {
        'name': 'part_status_threshold_breach',
        'short_name': 'Parts Stale',
        'tooltip': 'Be notified when a part stays in a status too long',
    },
    {
        'name': 'parts_ready',
        'short_name': 'Parts Ready',
        'tooltip': 'Be notified when all parts are ready for a service',
    },
    {
        'name': 'part_status_events',
        'short_name': 'Parts Status Changes',
        'tooltip': 'Be notified parts change status to a status configured to notify Service Writers',
    },
    {
        'name': 'customer_approval_response',
        'short_name': 'Customer Approval',
        'tooltip': 'Be notified when a customer responds to a request for approval',
    },
    {
        'name': 'update_location',
        'short_name': 'Location Transfer',
        'tooltip': 'Be notified when a service order is transferred to a different location',
    },
    {
        'name': 'online_payment',
        'short_name': 'Online Payment',
        'tooltip': 'Be notified when a payment is completed online',
    },
]

const service_specialist_notifications = [
    {
        'name': 'service_order_status_events',
        'short_name': 'SO Status Changes',
        'tooltip': 'Be notified when Service Orders change status to a status configured to notify Service Parts Specialists',
    },
    {
        'name': 'part_status_threshold_breach',
        'short_name': 'Parts Stale',
        'tooltip': 'Be notified when a part stays in a status too long',
    },
    {
        'name': 'part_status_events',
        'short_name': 'Parts Status Changes',
        'tooltip': 'Be notified parts change status to a status configured to notify Service Parts Specialists',
    }
]

const notifications_groups = [
    {
        'role': 'permissions_service_manager',
        'role_name': "Service Manager",
        'notification_category': 'service_manager',
        'notifications': service_manager_notifications
    },
    {
        'role': 'permissions_service_writer',
        'role_name': "Service Writer",
        'notification_category': 'service_writer',
        'notifications': service_writer_notifications
    },
    {
        'role': 'permissions_service_parts_specialist',
        'role_name': "Service Parts Specialist",
        'notification_category': 'service_parts_specialist',
        'notifications': service_specialist_notifications
    }
]

export default function UserNotificationsPreferencesModal({
    open,
    closeModal,
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [my_user, set_my_user] = useTrackedUser();
    const [notifications, setNotifications] = React.useState({})
    const [isLoading, setIsLoading] = React.useState(false)

    const handleChange = (permission, location_id, preference) => (event) => {
        if (notifications[permission] === undefined) {
            notifications[permission] = {}
        }
        if (notifications[permission][location_id] === undefined) {
            notifications[permission][location_id] = {}
        }
        notifications[permission][location_id][preference] = event.target.checked
        setNotifications({ ...notifications })
    }

    const save = () => {
        setIsLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(`my_user/notifications_preferences`, { notifications_preferences: notifications }).then((client) => {
                enqueueSnackbar('Successfully updated notification preferences', { variant: 'success' })
                setIsLoading(false)
                set_my_user({ ...my_user, notifications_preferences: notifications })
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to update notification preferences <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }


    React.useEffect(() => {
        setNotifications(JSON.parse(JSON.stringify(my_user.notifications_preferences))) // Need to dereference to proxy object
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    return (
        (<div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                maxWidth='lg'
                fullWidth
            >
                <LoadingBackdropIndeterminate
                    open={isLoading}
                    text={"Saving Preferences"}
                />
                <DialogTitle>
                    {"Notification Preferences"}
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            // justifyContent: "center",
                            // alignItems: "center",
                            gap: 3,
                            pt: '13px',
                            // textAlign: 'center'
                        }}
                    >
                        <Box>
                            <span>Email Digest</span><br />
                            <FormControl sx={{ m: 1, mb: 0 }} size="small">
                                <Select
                                    labelId="daily-digest-label"
                                    id="daily-digest-select"
                                    value={notifications?.daily_digest ?? 'weekly'}
                                    onChange={(e => {
                                        notifications['daily_digest'] = e.target.value
                                        setNotifications({ ...notifications })
                                    })}
                                    sx={{ maxWidth: '250px' }}
                                >
                                    <MenuItem key={'None'} value={'none'}>
                                        <span>None</span>
                                    </MenuItem>
                                    <MenuItem key={'Daily'} value={'daily'}>
                                        <span>Daily</span>
                                    </MenuItem>
                                    <MenuItem key={'Weekly'} value={'weekly'}>
                                        <span>Weekly</span>
                                    </MenuItem>
                                </Select>
                                <FormHelperText>Receive a regular digest of activity</FormHelperText>
                            </FormControl>
                        </Box>
                        <Divider />

                        <Box><span>Set the notifications you wish to receive</span></Box>
                        {notifications_groups.filter((x) => my_user[x.role]).map((role, idx) =>
                            <Box key={idx}>
                                <span><b>{role.role_name}:</b></span><br />
                                {my_user.locations.length === 0 && <span>Not assigned to any locations</span>}
                                {my_user.locations.map((loc, idx) =>
                                    <Box key={loc.id}>
                                        <Grid container alignItems={'center'}>
                                            <Grid size={2}>
                                                <span>{loc.name}:</span>
                                            </Grid>
                                            <Grid container size="grow" columns={{ xs: 3, sm: 5, md: Math.min(role.notifications.length, 7), lg: Math.min(role.notifications.length, 7), xl: Math.min(role.notifications.length, 7) }} alignItems={'center'}>
                                                {role.notifications.map((n) =>
                                                    <Grid key={n.name} size={1} sx={{ textAlign: 'center' }}>
                                                        <Tooltip
                                                            title={n.tooltip}
                                                            arrow
                                                        >
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={notifications?.[role.notification_category]?.[loc.id]?.[n.name] ?? true}
                                                                        onChange={handleChange(role.notification_category, loc.id, n.name)}
                                                                    />
                                                                }
                                                                label={n.short_name}
                                                                labelPlacement="bottom"
                                                            />
                                                        </Tooltip>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Grid>
                                        {my_user.locations.length - 1 > idx &&
                                            <Divider />
                                        }
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 10px 10px 30px" }} disabled={Object.keys(notifications).length === 0}>
                        <Grid size="auto">
                        </Grid>
                        <Grid size="auto">
                            <Button variant="contained" autoFocus onClick={save}>
                                Update
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
        </div>)
    );
}
